/**
 * A form section that declares a RELATED list, placed against the slots the
 * record already derives — the console half of #1466's defect fix.
 *
 * `core.form.sections[].related` has been governed since `2026080301`: the lint's
 * rule 6 refuses a bare string, an unregistered `rel_type`, or a direction other
 * than in/out. What it could not check is whether anything ever DREW one, and
 * nothing did — `buildForm` read `title` and `fields` and dropped the rest, so a
 * definition could name a related list that no surface could show. (Worse than
 * silence, as it turned out: the grammar makes `fields` optional for exactly
 * that section, so the migration's own example threw before it could be
 * ignored. Measured; see `packages/experience/test/formOrder.test.ts`.)
 *
 * ── This does NOT derive anything ───────────────────────────────────────────
 * The slots come from `relations.ts`, off `core.relationship_type` (#484), and
 * stay the single derivation of "what can be related to this record". A declared
 * section only says WHERE one of them is placed and under WHAT TITLE — the very
 * reconciliation `2026080301` retired the four-entry lookup table for. A spec
 * that matches no slot places nothing: the relationship is gone from the
 * metamodel, and a titled empty section would put the lint's finding on screen
 * as a hole.
 */
import type { FormView, RelatedSectionSpec } from '@solum/experience'
import type { RelationSlot } from './relations'

/** A related list the form document asks for, in document order. */
export interface DeclaredRelated {
  /** the section title the definition wrote */
  title: string
  description?: string
  relType: string
  direction: 'in' | 'out'
}

/** One entry of the cascade: a slot, and the title the definition gave it (if any). */
export interface ArrangedRelation {
  slot: RelationSlot
  /** the definition's title, or absent to keep the slot's own derived label */
  title?: string
  description?: string
}

/** The related lists this form declares, in the order the document wrote them. */
export function declaredRelated(view: FormView | null | undefined): DeclaredRelated[] {
  return (view?.sections ?? []).flatMap((sec) => sec.related
    ? [{
        title: sec.title,
        ...(sec.description ? { description: sec.description } : {}),
        relType: sec.related.rel_type,
        direction: sec.related.direction,
      }]
    : [])
}

/**
 * THE match rule: does one `sections[].related` spec name this derived slot?
 *
 * ── Why this is a named export and not two inline expressions ───────────────
 * It was two inline expressions, and they were the same rule written twice.
 * `crm/RecordPage.tsx` carries both copies today — `sectionForSlot`
 * (`RecordPage.tsx:100-103`, section from slot) and the `derivedSlots.find` in
 * the related-tab render (`RecordPage.tsx:260-262`, slot from section) — and
 * they are not even written the same way round, which is exactly how one gets
 * fixed and the other does not. Adding `slotsFor` beside them would have made
 * THREE. The skill names two copies of one match rule as the failure mode, so
 * the rule lives here once, and `slotsFor` below is a filter over it.
 *
 * `RecordPage.tsx` is claimed by another lane and is not edited here; it already
 * imports `RegisteredRelationSection` from `../record/RelationCascade`, so
 * importing this predicate is a path that exists and a follow-up that costs two
 * expressions. Until it does, the duplicate copies are LIVE — see the lane's
 * hand-off.
 *
 * ── The rule itself, and the two cases that are not obvious ─────────────────
 * A SYMMETRIC self-referential relationship is a single reading and its slot
 * carries `direction: 'both'` (`relations.ts` — emitting one per direction split
 * a record's partners across two chips that each saw half). The document has no
 * way to say "both": rule 6 accepts in/out only. So either direction matches a
 * `both` slot, because there is one thing to match and refusing it would make a
 * governed, lint-clean declaration silently place nothing.
 *
 * A POLYMORPHIC relationship matches one slot per endpoint entity — an
 * agreement's parties are organizations AND people. That is why this is a
 * predicate and its caller decides how many matches are allowed, rather than a
 * `find`.
 *
 * The spec is taken in the DOCUMENT's vocabulary (`rel_type`, snake case), which
 * is what both `RecordPage.tsx` sites hold in `sec.related`, so the follow-up is
 * a substitution rather than a translation.
 */
export function relatedMatchesSlot(
  spec: RelatedSectionSpec,
  slot: Pick<RelationSlot, 'relType' | 'direction'>,
): boolean {
  return slot.relType === spec.rel_type
    && (slot.direction === spec.direction || slot.direction === 'both')
}

/** Which derived slots one declaration names — `relatedMatchesSlot` as a filter. */
export function slotsFor(slots: RelationSlot[], declaration: DeclaredRelated): RelationSlot[] {
  const spec: RelatedSectionSpec = { rel_type: declaration.relType, direction: declaration.direction }
  return slots.filter((slot) => relatedMatchesSlot(spec, slot))
}

/** The form's placement rank for each slot it actually names. Unmentioned
 * slots return no rank so populated/frequency/alphabetical remain live. */
export function declaredPositions(
  slots: RelationSlot[], declarations: DeclaredRelated[],
): Map<string, number> {
  const positions = new Map<string, number>()
  for (const declaration of declarations) {
    for (const slot of slotsFor(slots, declaration)) {
      if (!positions.has(slot.id)) positions.set(slot.id, positions.size)
    }
  }
  return positions
}

/**
 * The cascade's order: every DECLARED list first, in document order, then every
 * remaining slot in the order it was given (populated → this user's frequency →
 * alphabetical, decided by `orderSlots`).
 *
 * Declaring one list must not remove the others. The related panel is derived
 * from the metamodel precisely so a new module's relationship appears with zero
 * console changes, and letting a form document act as an allow-list would put
 * that back behind an edit nobody remembers to make. A definition says what is
 * IMPORTANT here, not what exists.
 *
 * The declared title is used only when the declaration matches exactly ONE slot.
 * A polymorphic declaration matches several, and giving them all the same title
 * would produce sections a user cannot tell apart — the slot's own label already
 * carries the disambiguation (`Parties · People`).
 */
export function arrangeCascade(slots: RelationSlot[], declarations: DeclaredRelated[]): ArrangedRelation[] {
  const placed: ArrangedRelation[] = []
  const taken = new Set<string>()
  for (const declaration of declarations) {
    const matched = slotsFor(slots, declaration).filter((slot) => !taken.has(slot.id))
    for (const slot of matched) {
      taken.add(slot.id)
      placed.push(matched.length === 1
        ? { slot, title: declaration.title, ...(declaration.description ? { description: declaration.description } : {}) }
        : { slot })
    }
  }
  return [...placed, ...slots.filter((slot) => !taken.has(slot.id)).map((slot) => ({ slot }))]
}

/**
 * The form WITHOUT the sections that exist only to place a related list.
 *
 * `EntityForm` renders one `<fieldset>` per section with its title as the
 * `<legend>` (`form/EntityForm.tsx:299`), so a related-only section — no
 * `fields` at all, which is the whole shape `2026080301` documents — would draw
 * an empty titled box above a form: a legend, a border, and nothing in it, while
 * the list it names renders in the related panel. Splitting here rather than in
 * the form keeps "a related list is not a form control" as one statement in one
 * place, and leaves the renderer knowing only about fields.
 *
 * A section that declares BOTH fields and a related list keeps its fields on the
 * form; the list is placed by the cascade under the same title. That is the
 * document saying "these facts and that list belong together", and honouring
 * half of it would be the drop this slice exists to end.
 */
export function fieldSectionsOnly(view: FormView): FormView {
  const sections = view.sections.filter((sec) => !sec.related || sec.fields.length > 0)
  return sections.length === view.sections.length ? view : { ...view, sections }
}
